import { Modal, Typography } from '@loophealth/loop-ui-web-library';
import React from 'react';
import WithThemeProvider from '../../../theme/WithThemeProvider';
import {
    StyledCostPremiumIcon,
    StyledCostPremiumIconWrapper,
    StyledCostPremiumItem,
    StyledCostPremiumModal,
    StyledCostPremiumModalContent,
    StyledCostPremiumModalFooter,
    StyledCostPremiumModalPremium,
    StyledCostPremiumText
} from './styles';
import { ICostPremiumModal } from './types';
import { formatAmount } from '../../../utils/common';
import {
    StyledModularSummaryColumn,
    StyledModularSummaryColumnRight,
    StyledModularSummaryHorizontalDottedDivider,
    StyledModularSummaryPlanWallet
} from '../ModularEnrolmentSummary/styles';
import { WalletIcon } from '../../../assets/img';
import { BenefitPricingStructure, IAlacarte } from '../../../redux/slices/PolicyListSlice/types';
import { FlagsmithFlags, useFeatureFlag, useFeatureFlagValue } from '../../../utils/flagsmith';

const CostPremiumModal: React.FC<ICostPremiumModal> = ({
    setIsVisible,
    isVisible,
    modular,
    alacarte,
    priceSummary,
    title,
    isEnrolmentOpen
}) => {
    const toUseWallet = priceSummary?.walletDeducted ? priceSummary.walletDeducted > 0 : false;

    const getSubText = (plan: IAlacarte): string => {
        const isPerLife = plan.pricingStructure === BenefitPricingStructure.PER_LIFE;
        const hasTiers = plan.subCategories?.length > 0;
        return isPerLife
            ? hasTiers
                ? plan.subCategories.map((c) => `${c.selectedUserIds?.length}x ${c.name}`).join(', ')
                : `${plan.selectedUserIds?.length}x`
            : hasTiers
                ? plan.subCategories[0].name
                : '';
    };

    const isPaymentInstallmentsEnabled = useFeatureFlag(FlagsmithFlags.PAYMENT_INSTALLMENTS);
    const paymentInstallments = useFeatureFlagValue(FlagsmithFlags.PAYMENT_INSTALLMENTS) as string;
    const paymentInstallmentsNumber = parseInt(paymentInstallments, 10);
    const installmentsCount = isNaN(paymentInstallmentsNumber) ? 3 : paymentInstallmentsNumber;
    const hasInstallmentsText = isNaN(paymentInstallmentsNumber) && !!paymentInstallments.length;

    return (
        <Modal isVisible={isVisible} setIsVisible={setIsVisible} title={title} disablePadding>
            <WithThemeProvider>
                <StyledCostPremiumModal>
                    <StyledCostPremiumModalContent>
                        {modular ? (
                            <StyledCostPremiumItem>
                                <StyledCostPremiumIconWrapper>
                                    <StyledCostPremiumIcon src={modular.logo} />
                                    <StyledCostPremiumText>
                                        <Typography variant="small" weight="semiBold">
                                            {modular.name}
                                        </Typography>
                                    </StyledCostPremiumText>
                                </StyledCostPremiumIconWrapper>
                                <Typography variant="small" weight="semiBold">
                                    {formatAmount(modular.price)}
                                </Typography>
                            </StyledCostPremiumItem>
                        ) : null}
                        {alacarte.map((plan) => {
                            const subText = getSubText(plan);
                            return (
                                <StyledCostPremiumItem key={plan.id}>
                                    <StyledCostPremiumIconWrapper>
                                        <StyledCostPremiumIcon src={plan.logo} />
                                        <StyledCostPremiumText>
                                            <Typography variant="small" weight="semiBold">
                                                {plan.name}
                                            </Typography>
                                            {subText ? (
                                                <Typography variant="small" color="secondary">
                                                    {subText}
                                                </Typography>
                                            ) : null}
                                        </StyledCostPremiumText>
                                    </StyledCostPremiumIconWrapper>
                                    <Typography variant="small" weight="semiBold">
                                        {formatAmount(plan.price)}
                                    </Typography>
                                </StyledCostPremiumItem>
                            );
                        })}
                    </StyledCostPremiumModalContent>
                    <StyledModularSummaryHorizontalDottedDivider />
                    {priceSummary && (
                        <StyledCostPremiumModalFooter>
                            <StyledCostPremiumModalPremium>
                                <StyledCostPremiumItem>
                                    <Typography variant="small" weight="medium">
                                        Total Premium Amount
                                    </Typography>
                                    <Typography variant="small" weight="medium">
                                        {formatAmount(priceSummary.totalPremium)}
                                    </Typography>
                                </StyledCostPremiumItem>
                                {toUseWallet && (
                                    <StyledCostPremiumItem>
                                        <StyledModularSummaryPlanWallet>
                                            <Typography variant="small" weight="medium">
                                                Wallet Balance Used
                                            </Typography>
                                            <img src={WalletIcon} alt="wallet" />
                                        </StyledModularSummaryPlanWallet>
                                        <Typography variant="small" weight="medium" color="error">
                                            - {formatAmount(priceSummary.walletDeducted)}
                                        </Typography>
                                    </StyledCostPremiumItem>
                                )}
                            </StyledCostPremiumModalPremium>
                            <StyledModularSummaryHorizontalDottedDivider />
                            <StyledCostPremiumItem>
                                <StyledModularSummaryColumn>
                                    <Typography variant="large" weight="semiBold">
                                        To Pay
                                    </Typography>
                                    <Typography variant="small" color="secondary">
                                        (Inclusive of tax)
                                    </Typography>
                                </StyledModularSummaryColumn>
                                <StyledModularSummaryColumnRight>
                                    <Typography variant="large" weight="semiBold">
                                        {formatAmount(priceSummary.salaryDeducted)}
                                    </Typography>
                                    {isEnrolmentOpen && isPaymentInstallmentsEnabled && priceSummary.salaryDeducted ? (
                                        <Typography variant="small" color="secondary">
                                            {hasInstallmentsText
                                                ? paymentInstallments
                                                : `Amount will deducted from your salary in` +
                                                  ` ${installmentsCount} monthly instalments`}
                                        </Typography>
                                    ) : null}
                                </StyledModularSummaryColumnRight>
                            </StyledCostPremiumItem>
                        </StyledCostPremiumModalFooter>
                    )}
                </StyledCostPremiumModal>
            </WithThemeProvider>
        </Modal>
    );
};

export default CostPremiumModal;
